import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import { Save as SaveIcon } from "@material-ui/icons";
import { yupTrimToNull } from "src/utils/stringUtils";
import SimpleDropdown from "src/apps/commons/dropdowns/components/SimpleDropdown";
import { IRemyndBrand } from "../../remyndBrand/types";
import { IRemyndBrandPartnerMapSaveReq } from "../types";
import { EditModalState } from "./ListContainers";

type FormData = {
  nameTh: string;
  nameEn: string;
  remyndBrandId: RemyndBrandId;
};

const schema = yup.object().shape({
  remyndBrandId: yup
    .string()
    .transform(yupTrimToNull)
    .required("กรุณาเลือก Remynd Brand")
});

interface Props {
  editState: EditModalState;
  handleCloseModal: () => void;
  dropdowns: IDropdown<IRemyndBrand>[];
  callSubmit: (
    id: RemyndBrandPartnerMapId | undefined,
    req: IRemyndBrandPartnerMapSaveReq
  ) => Promise<boolean>;
  callDelete: (req: ICommonIdsReq) => Promise<boolean>;
}

const MappingModal: React.FC<Props> = props => {
  const { editState, dropdowns, handleCloseModal, callSubmit, callDelete } =
    props;

  const [dropdown, setDropdown] =
    useState<IDropdown<IRemyndBrand> | null>(null);

  const defaultFormData = useMemo<Partial<FormData>>(() => {
    return {
      nameTh: editState.partnerBrand?.nameTh ?? "",
      nameEn: editState.partnerBrand?.nameEn ?? "",
      remyndBrandId: editState.mapping?.remyndBrandId ?? ""
    };
  }, [editState]);

  const [submitting, setSubmitting] = useState(false);
  const {
    formState: { errors },
    handleSubmit,
    control,
    reset
  } = useForm<Partial<FormData>>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    reset(defaultFormData);
    if (editState.mapping) {
      setDropdown(
        dropdowns.find(d => d.value === editState.mapping?.remyndBrandId) ??
          null
      );
    } else {
      setDropdown(null);
    }
  }, [reset, editState, dropdowns, defaultFormData]);

  const [closing, setClosing] = useState(false);
  const handleRequestClose = () => setClosing(true);
  const handleCompleteClose = () => {
    handleCloseModal();
    setClosing(false);
    setDropdown(null);
  };

  const handleTransformAndSubmit = async (form: FormData) => {
    setSubmitting(true);
    const partnerBrandId = editState.partnerBrand?.id;
    const success = partnerBrandId
      ? await callSubmit(editState.mapping?.id, {
          remyndBrandId: form.remyndBrandId,
          partnerBrandId: partnerBrandId,
          snapPartnerNameTh: editState.partnerBrand?.nameTh,
          snapPartnerNameEn: editState.partnerBrand?.nameEn,
          snapRemyndNameTh: dropdown?.meta?.nameTh,
          snapRemyndNameEn: dropdown?.meta?.nameEn
        })
      : false;
    if (success) {
      handleRequestClose();
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    setSubmitting(true);
    const mappingId = editState.mapping?.id;
    if (mappingId) {
      const success = await callDelete({ ids: [mappingId] });
      if (success) {
        handleRequestClose();
      }
    }
    setSubmitting(false);
  };

  const isOpen = editState.open;
  const isAdd = !editState.mapping;
  return (
    <Dialog
      open={closing ? false : isOpen}
      onClose={handleRequestClose}
      onExited={handleCompleteClose}
      maxWidth="md"
      fullWidth
      disableBackdropClick
      disableEscapeKeyDown
      aria-labelledby="form-dialog-title"
    >
      <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
        <DialogTitle id="form-dialog-title" disableTypography>
          <Typography variant="h3">
            {`${isAdd ? "เพิ่ม" : "แก้ไข"} Mapping แบรนด์`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="nameTh"
                control={control}
                defaultValue={defaultFormData.nameTh}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled
                    {...field}
                    label="ชื่อแบรนด์ Partner (TH)"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="nameEn"
                control={control}
                defaultValue={defaultFormData.nameEn}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled
                    {...field}
                    label="ชื่อแบรนด์ Partner (EN)"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="remyndBrandId"
                control={control}
                defaultValue={defaultFormData.remyndBrandId}
                render={({ field: { onChange } }) => (
                  <SimpleDropdown
                    disabled={submitting}
                    label="Remynd Brand *"
                    options={dropdowns}
                    onChange={dropdown => {
                      onChange(dropdown?.value);
                      setDropdown(dropdown);
                    }}
                    value={dropdown}
                    error={Boolean(errors.remyndBrandId)}
                    helperText={errors.remyndBrandId?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleRequestClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          {editState?.mapping && (
            <Button
              variant="contained"
              onClick={handleDelete}
              disabled={submitting}
            >
              Delete Mapping
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={submitting}
            type="submit"
            startIcon={
              submitting ? (
                <CircularProgress color="secondary" size={20} />
              ) : (
                <SaveIcon />
              )
            }
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MappingModal;
